import { StoreIcon } from '@nimbus-ds/icons'
import { Select, Text } from '@nimbus-ds/components'
import { useQuery } from '@tanstack/react-query'
import { storeService } from '@/api/store'
import { authService } from '@/api/auth'

export function StoreSwitcher() {
  const currentStoreId = authService.getCurrentStoreId()

  // Get all stores the user has access to
  const { data: fetchedStores = [], isLoading } = useQuery({
    queryKey: ['user-stores'],
    queryFn: () => storeService.getAllStores(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
  // Uninstalled stores are not offered; the current one stays so the control never goes blank.
  const allStores = fetchedStores.filter((s) => !s.uninstalled_at || String(s.id) === currentStoreId)

  // Get current store info
  const { data: currentStore } = useQuery({
    queryKey: ['store-info', currentStoreId],
    queryFn: () => storeService.getStore(currentStoreId as string),
    enabled: !!currentStoreId,
  })

  const handleStoreChange = (storeId: string) => {
    // Update localStorage with new store_id
    try {
      const existing = localStorage.getItem('tnx_user_info')
      const userInfo = existing ? JSON.parse(existing) : {}
      userInfo.store_id = storeId
      localStorage.setItem('tnx_user_info', JSON.stringify(userInfo))

      // Reload the page to refresh all store-dependent data
      window.location.reload()
    } catch (error) {
      console.error('Failed to switch store:', error)
    }
  }

  const getCurrentStoreName = () => {
    if (!currentStore?.name) return 'TiendaNube'

    if (typeof currentStore.name === 'string') {
      return currentStore.name
    }

    // Handle localized names
    return currentStore.name.es ||
           currentStore.name.en ||
           Object.values(currentStore.name)[0] ||
           'TiendaNube'
  }

  const getStoreName = (store: any) => {
    if (!store?.name) return `Store ${store.id}`

    if (typeof store.name === 'string') {
      return store.name
    }

    return store.name.es ||
           store.name.en ||
           Object.values(store.name)[0] ||
           `Store ${store.id}`
  }

  if (isLoading || allStores.length <= 1) {
    // Show simple store indicator if loading or only one store
    return (
      <div className="flex items-center gap-2">
        <StoreIcon />
        <Text>{getCurrentStoreName()}</Text>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <StoreIcon />
      <Select
        id="store-switcher"
        name="store-switcher"
        value={currentStoreId || ''}
        onChange={(e) => handleStoreChange(e.target.value)}
      >
        {allStores.map((store) => (
          <Select.Option
            key={store.id}
            value={String(store.id)}
            label={`${getStoreName(store)}${store.domain ? ` · ${store.domain}` : ''} · ID: ${store.id}${store.uninstalled_at ? ' · desinstalada' : ''}`}
          />
        ))}
      </Select>
    </div>
  )
}
